import 'virtual:svg-icons-register';
import './utils/dayjs';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './components';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { isTransientAblyError } from './utils/ablyErrors';

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
		},
	},
});

if (import.meta.env.VITE_MODE !== 'development') {
	Sentry.init({
		dsn: import.meta.env.VITE_SENTRY_DCN,
		integrations: [new Sentry.BrowserTracing({})],
		tracesSampleRate: 0.2,
		environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
		ignoreErrors: [
			'TypeError: Failed to fetch',
			'TypeError: NetworkError when attempting to fetch resource.',
			// Safari's wording for the same network failure
			'TypeError: Load failed',
			// oidc-client-ts gave up on a slow network (silent renew retries)
			'ErrorTimeout: Network timed out',
			'AbortError: The operation was aborted.',
			'Error: No matching state found in storage',
			'No matching state found in storage',
		],
		beforeSend(event, hint) {
			// Ably hooks can leave transient errors as unhandled rejections.
			if (
				event.tags?.['ably.connection'] !== 'failed' &&
				isTransientAblyError(hint?.originalException)
			) {
				return null;
			}
			return event;
		},
		denyUrls: [
			//used to do not capture exception from third party recourses
			// Chrome extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^chrome-extension:\/\//i,
			// Errors inside Google Maps' own scripts
			/maps\.googleapis\.com\/maps-api-v3\//i,
		],
	});
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
	<BrowserRouter>
		<QueryClientProvider client={queryClient}>
			<ErrorBoundary>
				<App />
			</ErrorBoundary>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	</BrowserRouter>,
);
